// Helper: Get section HTML (uses global parseHTML from global.js)
function getSectionInnerHTML(html, selector = '.shopify-section') {
  const doc = parseHTML(html);
  const section = doc.querySelector(selector);
  return section ? section.innerHTML : '';
}

// Helper: Find button icon/spinner elements (supports both add-button and product-complete-the-look__add)
function findButtonElements(button) {
  if (!button) return { icon: null, spinner: null };
  
  // Try both class patterns
  const icon = button.querySelector('.add-button__icon, .product-complete-the-look__add-icon');
  const spinner = button.querySelector('.add-button__spinner, .product-complete-the-look__add-spinner');
  
  return { icon, spinner };
}

// Helper: Extract the already-loaded image URL from the product card that triggered the quick view.
// This avoids a redundant network request since the browser already has the card image cached.
function getCardImageUrl(triggerButton) {
  if (!triggerButton) return null;
  const card = triggerButton.closest('.card__image') || triggerButton.closest('.card-product__wrapper');
  if (!card) return null;
  // Grab the primary product image (skip video fallback images)
  const img = card.querySelector('.card-product__media img:not(.card-product__video-fallback)');
  if (!img) return null;
  // currentSrc reflects the actual loaded responsive image; fall back to src
  return img.currentSrc || img.src || null;
}

/**
 * Global Quick View Service
 * Can be called from anywhere to open a quick view modal for a product
 * Usage: window.QuickViewService.open(productHandle, variantId)
 */
window.QuickViewService = {
  modal: null,
  modalContent: null,
  loadingOverlay: null,
  skeleton: null,
  skeletonHTML: null,
  
  init() {
    this.modal = document.getElementById('product-quick-view-modal');
    this.modalContent = document.getElementById('product-quick-view-content');
    this.loadingOverlay = this.modal?.querySelector('.loading-overlay');
    this.skeleton = document.getElementById('product-quick-view-skeleton');
    // Store skeleton HTML for restoration
    if (this.skeleton) {
      this.skeletonHTML = this.skeleton.outerHTML;
    }
  },
  
  async open(productHandle, variantId = null, triggerButton = null, options = {}) {
    // Initialize if needed
    if (!this.modal) this.init();
    if (!this.modal || !productHandle) {
      console.error('QuickViewService: Modal not found or no product handle provided');
      return;
    }

    // Grab the already-loaded card image URL so we can reuse it in the quickview
    const cardImageUrl = getCardImageUrl(triggerButton);

    // Show loading spinner on button if provided
    if (triggerButton) {
      triggerButton.classList.add('loading');
      const { icon, spinner } = findButtonElements(triggerButton);
      if (icon) icon.style.display = 'none';
      if (spinner) spinner.style.display = 'flex';
      triggerButton.disabled = true;
    }

    // Show loading spinner in modal
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('hidden');
    }

    // Show skeleton and add loading class
    if (this.skeleton) {
      this.skeleton.classList.remove('hidden');
    }
    if (this.modal) {
      this.modal.classList.add('loading');
    }

    // Open modal (visible but waiting for content)
    if (typeof this.modal.open === 'function') {
      this.modal.open(false); // Pass false to not animate yet
    } else {
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('is-open');
      lockBodyScroll();
    }

    try {
      // Fetch quick view section
      const freeGiftParam = options.freeGift ? '&free_gift=1' : '';
      const freeGiftTierParam = options.freeGiftTier ? `&free_gift_tier=${encodeURIComponent(options.freeGiftTier)}` : '';
      const sectionUrl = `/products/${productHandle}?section_id=product-quick-view${variantId ? `&variant=${variantId}` : ''}${freeGiftParam}${freeGiftTierParam}`;
      const response = await fetch(sectionUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }
      
      const html = await response.text();
      const doc = parseHTML(html);
      const section = doc.querySelector('.shopify-section');
      if (!section) throw new Error('Section not found');
      
      const sectionContent = section.querySelector('.product-quick-view-modal__inner');
      if (!sectionContent) throw new Error('Content not found');
      
      // Extract variant script for variant-radios
      const variantScript = section.querySelector('variant-radios script[type="application/json"]');
      
      // Set the content
      if (this.modalContent) {
        // Preserve the close button
        const closeButton = this.modalContent.querySelector('.product-quick-view-modal__close-button');
        const closeButtonHTML = closeButton ? closeButton.outerHTML : null;
        
        // Set the new content
        this.modalContent.innerHTML = sectionContent.outerHTML;
        
        // Re-add the close button
        if (closeButtonHTML && this.modalContent) {
          this.modalContent.insertAdjacentHTML('afterbegin', closeButtonHTML);
        }
        
        // Re-inject variant script if needed
        if (variantScript && this.modalContent.querySelector('variant-radios')) {
          const variantRadios = this.modalContent.querySelector('variant-radios');
          const existingScript = variantRadios.querySelector('script[type="application/json"]');
          if (!existingScript && variantScript.textContent) {
            const newScript = document.createElement('script');
            newScript.type = 'application/json';
            newScript.textContent = variantScript.textContent;
            variantRadios.appendChild(newScript);
          }
        }

        // Reuse the already-loaded card image to avoid a new network request
        if (cardImageUrl) {
          const quickViewImg = this.modalContent.querySelector('.product-quick-view-modal__image');
          if (quickViewImg) {
            quickViewImg.src = cardImageUrl;
            quickViewImg.removeAttribute('loading'); // no need for lazy since it's cached
          }
        }
      }

      // Hide loading spinner and skeleton in modal
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.add('hidden');
      }
      if (this.skeleton) {
        this.skeleton.classList.add('hidden');
      }
      if (this.modal) {
        this.modal.classList.remove('loading');
      }

      // Reset trigger button
      if (triggerButton) {
        triggerButton.classList.remove('loading');
        const { icon, spinner } = findButtonElements(triggerButton);
        if (icon) icon.style.display = 'flex';
        if (spinner) spinner.style.display = 'none';
        triggerButton.disabled = false;
      }

      // Trigger slide-in animation after content is loaded
      requestAnimationFrame(() => {
        this.modal.classList.add('animating-in');
      });

      // Initialize variant selection or pre-launch Klaviyo embed (injected after page load)
      requestAnimationFrame(() => {
        const selectorDiv = this.modalContent?.querySelector('[data-quick-view-selector]');
        if (selectorDiv) {
          this.initVariantSelection();
          this.selectFirstAvailableSize();

          const sizeButtons = this.modalContent.querySelectorAll('.product-variant-picker__option');
          sizeButtons.forEach(btn => {
            btn.addEventListener('click', (e) => e.stopPropagation(), true);
          });

          const addToCartBtn = this.modalContent.querySelector('.product-form__submit');
          if (addToCartBtn) {
            addToCartBtn.addEventListener('click', (e) => e.stopPropagation(), true);
          }
        }

        if (window.initProductCountdownKlaviyo && this.modalContent?.querySelector('.product-countdown__klaviyo')) {
          window.initProductCountdownKlaviyo(this.modalContent, { dynamic: true });
        }
      });
      
    } catch (error) {
      console.error('QuickViewService: Error loading quick view:', error);
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.add('hidden');
      }
      if (this.skeleton) {
        this.skeleton.classList.add('hidden');
      }
      if (this.modal) {
        this.modal.classList.remove('loading');
      }
      if (this.modalContent) {
        this.modalContent.innerHTML = '<p style="padding: 20px; text-align: center;">Error loading product. Please try again.</p>';
      }
      
      // Reset trigger button on error
      if (triggerButton) {
        triggerButton.classList.remove('loading');
        const { icon, spinner } = findButtonElements(triggerButton);
        if (icon) icon.style.display = 'flex';
        if (spinner) spinner.style.display = 'none';
        triggerButton.disabled = false;
      }
    }
  },
  
  selectFirstAvailableSize() {
    if (!this.modalContent) return;
    if (!this.modalContent.querySelector('[data-quick-view-selector]')) return;

    const sizeInputs = this.modalContent.querySelectorAll('input[name="Size"]:not([disabled])');
    if (sizeInputs.length === 0) return;

    let firstAvailable = null;
    for (const input of sizeInputs) {
      const label = input.closest('label');
      if (label && !label.classList.contains('product-variant-picker__option--unavailable')) {
        firstAvailable = input;
        break;
      }
    }

    if (firstAvailable && !firstAvailable.checked) {
      firstAvailable.checked = true;
      firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
    }
  },
  
  /**
   * Initialize variant selection handlers for quick view modal
   * This replaces the inline script that doesn't execute when loaded via innerHTML
   */
  initVariantSelection() {
    if (!this.modalContent) return;
    
    const selectorDiv = this.modalContent.querySelector('[data-quick-view-selector]');
    if (!selectorDiv) return;
    
    const form = this.modalContent.querySelector('form');
    if (!form) return;
    
    const variantInput = form.querySelector('[data-variant-input]');
    const addButton = form.querySelector('[data-add-button]');
    const buttonText = form.querySelector('[data-button-text]');
    const priceContainer = this.modalContent.querySelector('.product-quick-view-modal__price');
    
    // Handle size radio changes
    selectorDiv.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', function() {
        const variantId = this.dataset.variantId;
        const label = this.closest('label');
        const isAvailable = label?.dataset.variantAvailable === 'true';
        const variantPrice = label?.dataset.variantPrice;
        const variantCompareAtPrice = label?.dataset.variantCompareAtPrice;
        
        if (variantId && variantInput) {
          variantInput.value = variantId;
          
          // Update button state
          if (addButton && buttonText) {
            if (isAvailable) {
              addButton.disabled = false;
              buttonText.textContent = 'ADD TO CART';
            } else {
              addButton.disabled = true;
              buttonText.textContent = 'SOLD OUT';
            }
          }
          
          // Update price if available
          if (priceContainer && variantPrice && window.Shopify && window.Shopify.formatMoney) {
            const price = parseInt(variantPrice, 10);
            const compareAtPrice = variantCompareAtPrice ? parseInt(variantCompareAtPrice, 10) : null;
            
            // Update price container - preserve structure but update values
            const priceItems = priceContainer.querySelectorAll('.price-item');
            const priceRegular = priceContainer.querySelector('.price__regular');
            const priceSale = priceContainer.querySelector('.price__sale');
            
            if (compareAtPrice && compareAtPrice > price) {
              // Show sale price
              if (priceSale) {
                priceSale.style.display = '';
                const regularPriceEl = priceSale.querySelector('.price-item--regular');
                const salePriceEl = priceSale.querySelector('.price-item--sale');
                if (regularPriceEl) regularPriceEl.textContent = window.Shopify.formatMoney(compareAtPrice);
                if (salePriceEl) salePriceEl.textContent = window.Shopify.formatMoney(price);
              }
              if (priceRegular) priceRegular.style.display = 'none';
            } else {
              // Show regular price
              if (priceRegular) {
                priceRegular.style.display = '';
                const regularPriceEl = priceRegular.querySelector('.price-item--regular');
                if (regularPriceEl) regularPriceEl.textContent = window.Shopify.formatMoney(price);
              }
              if (priceSale) priceSale.style.display = 'none';
            }
          }
        }
      });
    });
  },
  
  close() {
    if (!this.modal) return;
    
    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.classList.remove('is-open');
      unlockBodyScroll();
    }

    if (this.modalContent) {
      // Restore skeleton for next open
      const closeButton = this.modalContent.querySelector('.product-quick-view-modal__close-button');
      const closeButtonHTML = closeButton ? closeButton.outerHTML : '';
      this.modalContent.innerHTML = closeButtonHTML + (this.skeletonHTML || '');
      // Re-initialize skeleton reference
      this.skeleton = document.getElementById('product-quick-view-skeleton');
      if (this.skeleton) {
        this.skeleton.classList.add('hidden');
      }
    }
  }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.QuickViewService.init());
} else {
  window.QuickViewService.init();
}

if (!customElements.get('quick-view')) {

class QuickView extends HTMLElement {
  constructor() {
    super();
    // Support both add-button (product cards) and product-complete-the-look__add (complete the look)
    this.toggleButton = this.querySelector('button.add-button, button.product-complete-the-look__add');
    this.modal = document.getElementById('product-quick-view-modal');
    this.modalContent = document.getElementById('product-quick-view-content');
    this.loadingOverlay = this.modal?.querySelector('.loading-overlay');
    this.skeleton = document.getElementById('product-quick-view-skeleton');
    // Store skeleton HTML for restoration
    if (this.skeleton) {
      this.skeletonHTML = this.skeleton.outerHTML;
    }
    this.modalComponent = this.modal; // The custom element itself
    this.productHandle = this.dataset.productHandle;
    this.variantId = this.dataset.variantId;
  }

  connectedCallback() {
    this.openModal = this.openModal.bind(this);
    
    if (this.toggleButton) {
      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openModal();
      };
      
      this.toggleButton.addEventListener('click', clickHandler, true);
    }
  }

  async openModal() {
    if (!this.modal) {
      this.modal = document.getElementById('product-quick-view-modal');
      this.modalComponent = this.modal;
    }
    
    if (!this.modal || !this.productHandle) return;

    // Grab the already-loaded card image URL so we can reuse it in the quickview
    const cardImageUrl = getCardImageUrl(this.toggleButton);

    // Show loading spinner on button
    if (this.toggleButton) {
      this.toggleButton.classList.add('loading');
      const { icon, spinner } = findButtonElements(this.toggleButton);
      if (icon) icon.style.display = 'none';
      if (spinner) spinner.style.display = 'flex';
      this.toggleButton.disabled = true;
    }

    // Show loading spinner in modal
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('hidden');
    }

    // Show skeleton and add loading class
    if (this.skeleton) {
      this.skeleton.classList.remove('hidden');
    }
    if (this.modal) {
      this.modal.classList.add('loading');
    }

    // Open modal but keep it off-screen (visible but translated down)
    if (this.modalComponent?.open) {
      this.modalComponent.open(false); // Pass false to indicate not to animate yet
    } else {
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('is-open');
      // Don't add animating-in yet - wait for data to load
      lockBodyScroll();
    }

    try {
      // Fetch quick view section
      const sectionUrl = `/products/${this.productHandle}?section_id=product-quick-view${this.variantId ? `&variant=${this.variantId}` : ''}`;
      const response = await fetch(sectionUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }
      
      const html = await response.text();
      const doc = parseHTML(html);
      const section = doc.querySelector('.shopify-section');
      if (!section) throw new Error('Section not found');
      
      const sectionContent = section.querySelector('.product-quick-view-modal__inner');
      if (!sectionContent) throw new Error('Content not found');
      
      // Extract and execute only variant-radios script
      const variantScript = section.querySelector('variant-radios script[type="application/json"]');
      
      // Set the content
      if (this.modalContent) {
        // Preserve the close button before replacing content
        const closeButton = this.modalContent.querySelector('.product-quick-view-modal__close-button');
        const closeButtonHTML = closeButton ? closeButton.outerHTML : null;
        
        // Set the new content
        this.modalContent.innerHTML = sectionContent.outerHTML;
        
        // Re-add the close button if it existed
        if (closeButtonHTML && this.modalContent) {
          this.modalContent.insertAdjacentHTML('afterbegin', closeButtonHTML);
        }
        
        // Re-inject variant script if needed for variant-radios component
        if (variantScript && this.modalContent.querySelector('variant-radios')) {
          const variantRadios = this.modalContent.querySelector('variant-radios');
          const existingScript = variantRadios.querySelector('script[type="application/json"]');
          if (!existingScript && variantScript.textContent) {
            const newScript = document.createElement('script');
            newScript.type = 'application/json';
            newScript.textContent = variantScript.textContent;
            variantRadios.appendChild(newScript);
          }
        }

        // Reuse the already-loaded card image to avoid a new network request
        if (cardImageUrl) {
          const quickViewImg = this.modalContent.querySelector('.product-quick-view-modal__image');
          if (quickViewImg) {
            quickViewImg.src = cardImageUrl;
            quickViewImg.removeAttribute('loading'); // no need for lazy since it's cached
          }
        }
      }

      // Hide loading spinner and skeleton in modal
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.add('hidden');
      }
      if (this.skeleton) {
        this.skeleton.classList.add('hidden');
      }
      if (this.modal) {
        this.modal.classList.remove('loading');
      }

      // Hide loading spinner on button
      if (this.toggleButton) {
        this.toggleButton.classList.remove('loading');
        const { icon, spinner } = findButtonElements(this.toggleButton);
        if (icon) icon.style.display = 'flex';
        if (spinner) spinner.style.display = 'none';
        this.toggleButton.disabled = false;
      }

      // Now trigger the slide-in animation after content is loaded
      requestAnimationFrame(() => {
        this.modal.classList.add('animating-in');
      });

      // Initialize variant radios or pre-launch Klaviyo embed (skip pre-launch countdown modal)
      requestAnimationFrame(() => {
        const selectorDiv = this.modalContent?.querySelector('[data-quick-view-selector]');
        if (selectorDiv) {
          this.selectFirstAvailableSize();

          const sizeButtons = this.modalContent.querySelectorAll('.product-variant-picker__option');
          sizeButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
              e.stopPropagation();
            }, true);
          });

          const addToCartBtn = this.modalContent.querySelector('.product-form__submit');
          if (addToCartBtn) {
            addToCartBtn.addEventListener('click', (e) => {
              e.stopPropagation();
            }, true);
          }
        }

        if (window.initProductCountdownKlaviyo && this.modalContent?.querySelector('.product-countdown__klaviyo')) {
          window.initProductCountdownKlaviyo(this.modalContent, { dynamic: true });
        }
      });
      
    } catch (error) {
      console.error('Error loading quick view:', error);
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.add('hidden');
      }
      if (this.skeleton) {
        this.skeleton.classList.add('hidden');
      }
      if (this.modal) {
        this.modal.classList.remove('loading');
      }
      if (this.modalContent) {
        this.modalContent.innerHTML = '<p>Error loading product. Please try again.</p>';
      }
      
      // Hide loading spinner on button on error
      if (this.toggleButton) {
        this.toggleButton.classList.remove('loading');
        const { icon, spinner } = findButtonElements(this.toggleButton);
        if (icon) icon.style.display = 'flex';
        if (spinner) spinner.style.display = 'none';
        this.toggleButton.disabled = false;
      }
    }
  }

  closeModal() {
    if (!this.modal) return;

    // Use custom element's close method if available
    if (this.modalComponent && typeof this.modalComponent.close === 'function') {
      this.modalComponent.close();
    } else {
      // Fallback
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.classList.remove('is-open');
      unlockBodyScroll();
    }

    // Restore skeleton for next open
    if (this.modalContent) {
      const closeButton = this.modalContent.querySelector('.product-quick-view-modal__close-button');
      const closeButtonHTML = closeButton ? closeButton.outerHTML : '';
      this.modalContent.innerHTML = closeButtonHTML + (this.skeletonHTML || '');
      // Re-initialize skeleton reference
      this.skeleton = document.getElementById('product-quick-view-skeleton');
      if (this.skeleton) {
        this.skeleton.classList.add('hidden');
      }
    }
  }

  selectFirstAvailableSize() {
    if (!this.modalContent) return;
    if (!this.modalContent.querySelector('[data-quick-view-selector]')) return;

    // Find first available size option
    const sizeInputs = this.modalContent.querySelectorAll('input[name="Size"]:not([disabled])');
    if (sizeInputs.length === 0) return;

    // Find first available size (not in disabled wrapper)
    let firstAvailable = null;
    for (const input of sizeInputs) {
      const label = input.closest('label');
      if (label && !label.classList.contains('product-variant-picker__option--unavailable')) {
        firstAvailable = input;
        break;
      }
    }

    if (firstAvailable && !firstAvailable.checked) {
      firstAvailable.checked = true;
      firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

}

customElements.define('quick-view', QuickView);

} // end if (!customElements.get('quick-view'))

// Document-level event delegation as fallback for button clicks
// This ensures clicks work even if custom element hasn't initialized
// Uses the global QuickViewService for consistent behavior
document.addEventListener('click', (e) => {
  // Support both add-button (product cards) and product-complete-the-look__add (complete the look)
  const button = e.target.closest('quick-view .add-button, quick-view .product-complete-the-look__add, .add-button, .product-complete-the-look__add');
  if (button && button.closest('quick-view')) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    const quickView = button.closest('quick-view');
    const productHandle = quickView?.dataset.productHandle;
    const variantId = quickView?.dataset.variantId;
    
    if (productHandle && window.QuickViewService) {
      // Use global service for consistent behavior across all contexts
      window.QuickViewService.open(productHandle, variantId, button);
    } else if (quickView && typeof quickView.openModal === 'function') {
      // Fallback to custom element method
      quickView.openModal();
    }
  }
}, true); // Use capture phase

// Also listen for custom event to open quick view programmatically
document.addEventListener('quick-view:open', (e) => {
  const { productHandle, variantId, triggerButton } = e.detail || {};
  if (productHandle && window.QuickViewService) {
    window.QuickViewService.open(productHandle, variantId, triggerButton);
  }
});
